import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  History, Zap, TrendingUp, ShieldAlert, Sparkles, Key, Workflow,
  CalendarClock, Layers, Target, Filter, RefreshCw, Loader2,
} from "lucide-react";

const KIND_META = {
  rule_fire:      { label: "Rule fired",          icon: Workflow,      color: "text-blue-600",     bg: "bg-blue-50",     border: "border-blue-200" },
  asc_promote:    { label: "ASC promotion",       icon: TrendingUp,    color: "text-emerald-600",  bg: "bg-emerald-50",  border: "border-emerald-200" },
  scale:          { label: "Winner scaled",       icon: TrendingUp,    color: "text-emerald-600",  bg: "bg-emerald-50",  border: "border-emerald-200" },
  circuit_trip:   { label: "Circuit breaker",     icon: ShieldAlert,   color: "text-red-600",      bg: "bg-red-50",      border: "border-red-200" },
  kw_promote:     { label: "Keyword promote",     icon: Key,           color: "text-purple-600",   bg: "bg-purple-50",   border: "border-purple-200" },
  auto_op:        { label: "Auto-Operator",       icon: Sparkles,      color: "text-indigo-600",   bg: "bg-indigo-50",   border: "border-indigo-200" },
  exclusion:      { label: "Exclusion applied",   icon: Target,        color: "text-amber-600",    bg: "bg-amber-50",    border: "border-amber-200" },
  matrix_launch:  { label: "Matrix launch",       icon: Layers,        color: "text-slate-700",    bg: "bg-slate-100",   border: "border-slate-300" },
  dayparting:     { label: "Dayparting",          icon: CalendarClock, color: "text-cyan-600",     bg: "bg-cyan-50",     border: "border-cyan-200" },
};

const fmtMoney = (n) => "$" + Number(n || 0).toFixed(2);
const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtRelative = (iso) => {
  if (!iso) return "";
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return `${Math.round(delta)}s ago`;
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
  return `${Math.round(delta / 86400)}d ago`;
};

export default function ActionHistory() {
  const [events, setEvents] = useState([]);
  const [byKind, setByKind] = useState({});
  const [days, setDays] = useState(7);
  const [kindFilter, setKindFilter] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (kindFilter) params.set("kind", kindFilter);
      const { data } = await api.get(`/action-history?${params.toString()}`);
      setEvents(data.events || []);
      setByKind(data.by_kind || {});
    } catch (e) {
      toast.error("Failed to load action history");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days, kindFilter]);

  return (
    <div className="p-6 max-w-[1100px]" data-testid="action-history-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <History className="w-5 h-5 text-indigo-600" /> Action History
        </h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Unified audit log of every automated + manual change to your campaigns.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <button
          onClick={() => setKindFilter(null)}
          data-testid="ah-filter-all"
          className={`text-[11px] rounded px-2 py-1 border ${!kindFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"}`}
        >
          All ({events.length})
        </button>
        {Object.entries(byKind).map(([k, cnt]) => {
          const meta = KIND_META[k] || { label: k, color: "text-slate-700" };
          const active = kindFilter === k;
          return (
            <button
              key={k}
              onClick={() => setKindFilter(active ? null : k)}
              data-testid={`ah-filter-${k}`}
              className={`text-[11px] rounded px-2 py-1 border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-100"}`}
            >
              <span className={active ? "" : meta.color}>{meta.label}</span>
              <span className="ml-1 text-slate-400 font-mono">{cnt}</span>
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="ah-range"
            className="bg-white border border-slate-200 rounded-md px-2 py-1.5 text-xs"
          >
            {[1, 7, 30, 90].map((d) => <option key={d} value={d}>Last {d}d</option>)}
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="ah-refresh"
            className="flex items-center gap-1 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 rounded-md px-2 py-1.5 text-xs"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-2" data-testid="ah-timeline">
        {loading && events.length === 0 && (
          <div className="text-center py-16 text-slate-500 text-xs font-mono">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" /> Loading…
          </div>
        )}
        {!loading && events.length === 0 && (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-md">
            <Zap className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-600 font-medium">Nothing has happened yet</p>
            <p className="text-xs text-slate-500 mt-1 font-mono">
              Rule fires, ASC promotes, winner scales, and manual changes will appear here.
            </p>
          </div>
        )}
        {events.map((e, i) => {
          const meta = KIND_META[e.kind] || { label: e.kind, icon: Zap, color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" };
          const Icon = meta.icon;
          return (
            <div
              key={`${e.kind}-${i}`}
              data-testid={`ah-event-${e.kind}-${i}`}
              className={`bg-white border rounded-md p-3 flex items-start gap-3 ${meta.border}`}
            >
              <div className={`shrink-0 w-9 h-9 rounded-md ${meta.bg} ${meta.color} flex items-center justify-center`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-medium text-slate-900 truncate">{e.title}</div>
                  <div className="text-[10px] text-slate-400 font-mono shrink-0" title={fmtTime(e.at)}>
                    {fmtRelative(e.at)}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
                  target: {e.target} · action: {e.action}
                  {e.value !== null && e.value !== undefined && (
                    <span className="ml-1">· value: <span className="text-slate-700">{typeof e.value === "number" ? (e.value > 10 ? fmtMoney(e.value) : e.value) : e.value}</span></span>
                  )}
                </div>
                {e.detail && (
                  <div className="text-[11px] text-slate-600 mt-1 break-words">{e.detail}</div>
                )}
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-wider ${meta.color} shrink-0`}>
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
